import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError } from './api'

/**
 * Whether the browser is on its way off this page.
 *
 * <p>
 * A navigation kills every request in flight, and the browser reports that to `fetch` as a
 * network failure rather than as an abort - so a screen that is being torn down draws
 * "Could not reach the server" over itself on the way out. Nobody can act on that: the page
 * reporting it is already gone.
 * </p>
 *
 * Module scope rather than per-hook, because the event is about the document, and a page
 * with six loaders on it should listen once.
 */
let leaving = false

if (typeof window !== 'undefined') {
  // pagehide rather than beforeunload: it fires for the back/forward cache too, and
  // beforeunload is the one browsers increasingly refuse to honour.
  window.addEventListener('pagehide', () => {
    leaving = true
  })

  // A page restored from the back/forward cache is a live page again, and its next failure
  // is a real one.
  window.addEventListener('pageshow', () => {
    leaving = false
  })
}

export interface AsyncState<T> {
  data: T | undefined
  error: ApiError | undefined
  loading: boolean
  /** Re-runs the loader, e.g. after a mutation. */
  reload: () => void
}

/**
 * Loads data for a screen and cancels in flight when the screen goes away.
 *
 * Deliberately small: a data-fetching library would be a dependency, a cache to
 * reason about and a migration later. Screens here load a list, mutate, and
 * reload - which is this, in forty lines.
 */
export function useAsync<T>(
  loader: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[],
): AsyncState<T> {
  const [data, setData] = useState<T | undefined>(undefined)
  const [error, setError] = useState<ApiError | undefined>(undefined)
  const [loading, setLoading] = useState(true)
  const [nonce, setNonce] = useState(0)

  // Held in a ref so a changing closure does not re-trigger the effect: the deps
  // the caller passes are the contract, not the identity of the function.
  const loaderRef = useRef(loader)
  loaderRef.current = loader

  useEffect(() => {
    const controller = new AbortController()
    let live = true

    setLoading(true)
    setError(undefined)

    loaderRef
      .current(controller.signal)
      .then((result) => {
        if (!live) return
        setData(result)
      })
      .catch((cause: unknown) => {
        // An aborted request is a screen that moved on, not a failure to report. Neither is
        // one the browser cancelled because it is leaving the page.
        if (!live || controller.signal.aborted || leaving) return

        setError(
          cause instanceof ApiError
            ? cause
            : new ApiError(0, 'Could not reach the server', 'Check your connection and try again.'),
        )
      })
      .finally(() => {
        if (live) setLoading(false)
      })

    return () => {
      live = false
      controller.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce])

  const reload = useCallback(() => setNonce((n) => n + 1), [])

  return { data, error, loading, reload }
}

/**
 * Folds several loads into one gate.
 *
 * <p>
 * A dialog that fetches a record and two catalogues is not ready until all three have
 * arrived - drawing the form when the first lands is what makes a checkbox list grow under
 * the pointer. The first failure is the one reported: three copies of "could not reach the
 * server" says no more than one.
 * </p>
 *
 * Reloading reloads all of them, because the caller cannot know which one failed.
 */
export function combine(
  ...states: readonly AsyncState<unknown>[]
): { loading: boolean; error: ApiError | undefined; reload: () => void } {
  return {
    loading: states.some((state) => state.loading),
    error: states.find((state) => state.error !== undefined)?.error,
    reload: () => states.forEach((state) => state.reload()),
  }
}
